<script setup lang="ts">
/**
 * `.scu-grid-item` 等价卡片（2026-06-25 grill R1/R6）。
 *
 * 复用场景：
 *   - `InnovationSection` 整张卡片即一个 GridItem（F12 后续把多个
 *     innovation Section 聚进一个 `.scu-widget` + `.scu-grid` 区，对齐
 *     模板「创新点」= 1 widget + 4 grid-item）。
 *   - `ImpactSection` 把每张 DataCard 渲染为一个 GridItem（对齐模板
 *     「应用效果」= 1 widget + 3 grid-item）。
 *
 * 视觉锚点：左侧 `▶` marker（伪元素，brand-primary）、h4 标题 + p 摘要
 * 层级、hover 上移 + 边框高亮。颜色 / 字号 / 间距全部走 token。
 */
import { computed } from 'vue';

import DraftBadge from '@/components/DraftBadge.vue';
import SummaryText from '@/components/SummaryText.vue';
import { hasText } from './section-presenters';

const props = defineProps<{
  readonly title?: string | null;
  readonly summary?: string | null;
  /** 预览模式下 Draft Section 挂草稿角标（F08，由 useDraftBadge 计算后传入）。 */
  readonly showDraftBadge?: boolean;
}>();

const hasTitle = computed(() => hasText(props.title));
const hasSummary = computed(() => hasText(props.summary));
</script>

<template>
  <article :class="$style.item" data-shell="scu-grid-item">
    <DraftBadge v-if="props.showDraftBadge" :class="$style.draftBadge" />
    <h4 v-if="hasTitle" :class="$style.title">{{ props.title }}</h4>
    <p v-if="hasSummary" :class="$style.summary"><SummaryText :text="props.summary" /></p>
    <slot />
  </article>
</template>

<style module>
.item {
  position: relative;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
  padding: var(--template-grid-item-padding-y) var(--template-grid-item-padding-right)
    var(--template-grid-item-padding-y) var(--template-grid-item-padding-left);
  transition:
    box-shadow var(--template-hover-duration) ease,
    transform var(--template-hover-duration) ease,
    border-color var(--template-hover-duration) ease;
}

.draftBadge {
  position: absolute;
  top: var(--space-1);
  right: var(--space-1);
  z-index: 1;
}

.item::before {
  position: absolute;
  left: var(--template-grid-marker-left);
  top: var(--template-grid-marker-top);
  color: var(--color-brand-primary);
  font-size: var(--font-size-sm);
  content: '▶';
}

.item:hover {
  border-color: var(--color-brand-primary);
  box-shadow: var(--shadow-card);
  transform: translateY(var(--template-hover-lift));
}

.title {
  margin: 0 0 var(--space-1);
  color: var(--color-brand-primary);
  font-size: var(--font-size-base);
  font-weight: 700;
}

.summary {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
  white-space: pre-wrap;
}
</style>
